import '../Integration/Integrations.scss'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import React, { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { OAuth2AppCard } from '../../components/OAuth2App/OAuth2AppCard/OAuth2AppCard'
import { type ShortOAuth2App, useListOAuth2AppsQuery } from '../../store/auth/auth'
import { type CustomIntegration } from '../../models/CustomIntegration'
import { useListCustomIntegrationsQuery } from '../../store/customIntegration/customIntegration'
import { useOAuthApps } from '../Integration/useOAuthApps'
import { useAppQuotaGate } from './useAppQuotaGate'
import { useCustomIntegrationQuota } from '../CustomIntegration/useCustomIntegrationQuota'
import { useCustomIntegrationScopes } from '../CustomIntegration/useCustomIntegrationScopes'
import { ManagedAtEnterpriseLevelInfo } from '../../components/ManagedAtEnterpriseLevel/ManagedAtEnterpriseLevelInfo'
import { Upsell } from '../Plan/DeltaTrial/Upsell/Upsell'
import { DELTA_TRIAL_FEATURES, type DeltaTrialFeature } from '../Plan/DeltaTrial/DeltaTrialFeatures'
import { ScrollNavigation, type ScrollNavigationSection } from '../../components/NavigationWithSideBar/ScrollNavigation'
import { HowToUseAppsCard } from '../../components/OAuth2App/HowToUseAppsCard/HowToUseAppsCard'
import { CustomIntegrationCard } from '../../components/CustomIntegration/CustomIntegrationCard/CustomIntegrationCard'
import { AddAppCard } from '../../components/AppCard/AddAppCard'
import { Button } from '../../components/Button/Button'
import { DEVELOPER_DOCS_URL } from './constants'
import { CUSTOM_INTEGRATION_CREATE_PATH, isCustomIntegrationsFeatureEnabled } from '../CustomIntegration/constants'

interface AppDirectoryCategory {
  id: string
  label: string
  title: string
  matches: (app: ShortOAuth2App) => boolean
}

const workFlowAppNames = ['Zapier']

const OWN_APPS_CATEGORY_ID = 'custom'
const CUSTOM_INTEGRATIONS_SECTION_ID = 'custom-integrations'
const OAUTH2_APPS_UPSELL_SECTION_ID = 'apps-upsell'

const LEADING_CATEGORIES: AppDirectoryCategory[] = [
  { id: 'mcp', label: 'Zocks MCP', title: 'Zocks MCP', matches: (app) => app.category === 'mcp' },
  { id: 'workflow', label: 'Workflow', title: 'Workflow', matches: (app) => workFlowAppNames.includes(app.name) },
]

const TRAILING_CATEGORIES: AppDirectoryCategory[] = [
  {
    id: OWN_APPS_CATEGORY_ID,
    label: 'Your Apps',
    title: 'Your own apps',
    matches: (app) => app.category !== 'mcp' && (app.type === 'dev' || app.type === 'internal'),
  },
  {
    id: 'partner',
    label: 'Partner',
    title: 'Partner',
    matches: (app) => app.category !== 'mcp' && app.type === 'external' && !workFlowAppNames.includes(app.name),
  },
]

const DeveloperDocsLink: React.FC = () => (
  <a href={DEVELOPER_DOCS_URL} target='_blank' rel='noopener noreferrer' className='text-decoration-underline zocks-text-gray-900'>
    Developer docs
  </a>
)

interface AddButtonProps {
  label: string
  onClick: () => void
}

const AddButton: React.FC<AddButtonProps> = ({ label, onClick }) => (
  <Button variant='primaryLinkButton' className='d-flex align-items-center gap-2 p-0 text-nowrap text-decoration-none button-link-medium' onClick={onClick}>
    <i className='bi bi-plus-circle' />
    {label}
  </Button>
)

const CardColumn: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Col xs={12} sm={6} xxl={3} className='d-flex justify-content-center integration-settings__common-cards'>
    {children}
  </Col>
)

interface OwnAppsSectionOptions {
  isAppCreationEnabled: boolean
  onAddNewApp: () => void
}

const createAppSection = (category: AppDirectoryCategory, apps: ShortOAuth2App[], ownApps: OwnAppsSectionOptions): ScrollNavigationSection => {
  const showOwnAppsActions = category.id === OWN_APPS_CATEGORY_ID && ownApps.isAppCreationEnabled
  return {
    id: category.id,
    label: category.label,
    title: category.title,
    subtitle: showOwnAppsActions ? (
      <>
        Build a private app to connect your own tools and workflows to Zocks. <DeveloperDocsLink />
      </>
    ) : undefined,
    actions: showOwnAppsActions ? <AddButton label='Add New App' onClick={ownApps.onAddNewApp} /> : undefined,
    content: (
      <Container fluid className='px-0'>
        <Row className='g-3'>
          {apps.map((app) => (
            <CardColumn key={app.clientId}>
              <OAuth2AppCard oAuth2App={app} />
            </CardColumn>
          ))}
          {category.id === 'mcp' ? (
            <CardColumn>
              <HowToUseAppsCard />
            </CardColumn>
          ) : null}
          {showOwnAppsActions && !apps.length ? (
            <CardColumn>
              <AddAppCard label='Add new app' onClick={ownApps.onAddNewApp} />
            </CardColumn>
          ) : null}
        </Row>
      </Container>
    ),
  }
}

const createUpsellSection = (id: string, label: string, feature: DeltaTrialFeature): ScrollNavigationSection => ({
  id,
  label,
  title: label,
  content: <Upsell feature={feature} />,
})

const createCustomIntegrationSection = (
  customIntegrations: CustomIntegration[],
  canCreate: boolean,
  onAddCustomIntegration: () => void,
): ScrollNavigationSection => ({
  id: CUSTOM_INTEGRATIONS_SECTION_ID,
  label: 'Custom MCP',
  title: 'Custom MCP',
  subtitle: (
    <>
      Connect your external MCP to Zocks. <DeveloperDocsLink />
    </>
  ),
  actions: canCreate ? <AddButton label='Add custom MCP' onClick={onAddCustomIntegration} /> : undefined,
  content: (
    <Container fluid className='px-0'>
      <Row className='g-3'>
        {customIntegrations.map((customIntegration) => (
          <CardColumn key={customIntegration.id}>
            <CustomIntegrationCard customIntegration={customIntegration} />
          </CardColumn>
        ))}
        {!customIntegrations.length && canCreate ? (
          <CardColumn>
            <AddAppCard label='Add custom MCP server' onClick={onAddCustomIntegration} />
          </CardColumn>
        ) : null}
      </Row>
    </Container>
  ),
})

export const OAuth2AppDirectory: React.FC = () => {
  useDocumentTitle('Integrations')
  const navigate = useNavigate()

  const { oauth2AppsEnabled: oauth2Enabled, customIntegrationsEnabled, isFetching: quotaIsFetching, quotaSuccess } = useCustomIntegrationQuota()
  const { canList: canListCustomIntegrations, canCreate: canCreateCustomIntegrations } = useCustomIntegrationScopes()

  const accountId = useAppSelector(selectAccountId)
  const { data: oAuth2AppData, isLoading: oAuth2AppIsLoading } = useListOAuth2AppsQuery(
    { accountId },
    { refetchOnMountOrArgChange: true, skip: !oauth2Enabled },
  )
  const {
    data: customIntegrationData,
    isLoading: customIntegrationsAreLoading,
    isError: customIntegrationsFailed,
  } = useListCustomIntegrationsQuery(
    { accountId },
    { refetchOnMountOrArgChange: true, skip: !isCustomIntegrationsFeatureEnabled() || !customIntegrationsEnabled || !canListCustomIntegrations },
  )
  const { isOAuthAppAllowed, isAppCreationEnabled, accountIsLoading } = useOAuthApps()
  const { isAppQuotaSatisfied } = useAppQuotaGate()

  const oAuth2Apps: ShortOAuth2App[] = useMemo(() => {
    return (oAuth2AppData?.data ?? []).filter((app) => isOAuthAppAllowed(app.id) && isAppQuotaSatisfied(app.clientId))
  }, [oAuth2AppData, isOAuthAppAllowed, isAppQuotaSatisfied])

  const customIntegrations: CustomIntegration[] = useMemo(() => customIntegrationData?.data ?? [], [customIntegrationData])

  const handleAddNewApp = useCallback(() => {
    navigate('/account/integration-settings/oauth2apps/new')
  }, [navigate])

  const handleAddCustomIntegration = useCallback(() => {
    navigate(CUSTOM_INTEGRATION_CREATE_PATH)
  }, [navigate])

  const sections: ScrollNavigationSection[] = useMemo(() => {
    const ownApps: OwnAppsSectionOptions = { isAppCreationEnabled, onAddNewApp: handleAddNewApp }
    const appSections = (categories: AppDirectoryCategory[]): ScrollNavigationSection[] =>
      categories
        .map((category) => ({ category, apps: oAuth2Apps.filter(category.matches) }))
        .filter(({ category, apps }) => apps.length > 0 || (category.id === OWN_APPS_CATEGORY_ID && isAppCreationEnabled))
        .map(({ category, apps }) => createAppSection(category, apps, ownApps))

    const customIntegrationSection = (): ScrollNavigationSection[] => {
      if (!isCustomIntegrationsFeatureEnabled()) {
        return []
      }
      if (!customIntegrationsEnabled) {
        return quotaSuccess ? [createUpsellSection(CUSTOM_INTEGRATIONS_SECTION_ID, 'Custom MCP', DELTA_TRIAL_FEATURES['customIntegrations'])] : []
      }
      const showCustomIntegrations = canListCustomIntegrations && !customIntegrationsFailed && (customIntegrations.length > 0 || canCreateCustomIntegrations)
      return showCustomIntegrations ? [createCustomIntegrationSection(customIntegrations, canCreateCustomIntegrations, handleAddCustomIntegration)] : []
    }

    if (!oauth2Enabled) {
      const appsUpsell = quotaSuccess ? [createUpsellSection(OAUTH2_APPS_UPSELL_SECTION_ID, 'Zocks apps', DELTA_TRIAL_FEATURES['appDirectory'])] : []
      return [...appsUpsell, ...customIntegrationSection()]
    }
    return [...appSections(LEADING_CATEGORIES), ...customIntegrationSection(), ...appSections(TRAILING_CATEGORIES)]
  }, [
    oAuth2Apps,
    oauth2Enabled,
    customIntegrationsEnabled,
    quotaSuccess,
    customIntegrations,
    customIntegrationsFailed,
    canListCustomIntegrations,
    canCreateCustomIntegrations,
    isAppCreationEnabled,
    handleAddNewApp,
    handleAddCustomIntegration,
  ])

  const { hash } = useLocation()
  const rawSectionId = hash?.slice(1)
  const scrollToSectionId = sections.some((section) => section.id === rawSectionId) ? rawSectionId : undefined

  if (quotaIsFetching || oAuth2AppIsLoading || customIntegrationsAreLoading || accountIsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (!sections.length) {
    return <ManagedAtEnterpriseLevelInfo resourceName={'Apps'} />
  }

  return (
    <ScrollNavigation
      sections={sections}
      defaultActiveSectionId={sections[0]?.id ?? ''}
      scrollToSectionId={scrollToSectionId}
      sectionClassName='integration-settings__integrations-container'
      variant='compact'
    />
  )
}
